// ============================================================
//  ALTIMO Medical Center — Admision.jsx  (v3 — DEFINITIVO)
//  Fusión completa OLD v2 + fixes v2.2
//  Mejoras integradas:
//    ✓ Tipo de documento: DUI, Pasaporte, DPI, Carnet, etc.
//    ✓ Subtipo "Otro" habilita campo de texto libre
//    ✓ Todo el texto a MAYÚSCULAS automáticamente
//    ✓ DUI vacío se guarda como NULL (no viola UNIQUE)
//    ✓ tipo_ingreso sin CHECK constraint (ver migration)
//    ✓ Médico muestra título + nombre + apellido
//    ✓ Sección legal completa
//    ✓ Info de asignación automática en remisión
//    ✓ Dirección del familiar
//  Ubicación: src/components/Admision.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import Formularios from './Formularios';
import TooltipAyuda from './TooltipAyuda';

// ── Utilidades ──────────────────────────────────────────────
function calcularEdad(fechaNac) {
  if (!fechaNac) return '';
  const hoy = new Date(); const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
}
const mayus = (v) => (v || '').toUpperCase();

// Sanea el valor crudo de un <input type="date"> (formato YYYY-MM-DD).
// Algunos navegadores permiten temporalmente valores fuera de rango mientras
// el usuario teclea cada segmento (ej. mes=99), por lo que el valor no se
// guarda sin antes acotar día (01-31), mes (01-12) y año a un rango realista.
function sanearFechaInput(valor) {
  if (!valor) return '';
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return valor; // formato inesperado: no se toca, se deja pasar tal cual
  let [, anio, mes, dia] = m;
  let mesNum = Math.min(Math.max(parseInt(mes, 10) || 1, 1), 12);
  const anioNum = Math.min(Math.max(parseInt(anio, 10) || 1900, 1900), new Date().getFullYear());
  const diasEnMes = new Date(anioNum, mesNum, 0).getDate();
  const diaNum = Math.min(Math.max(parseInt(dia, 10) || 1, 1), diasEnMes);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(anioNum)}-${pad(mesNum)}-${pad(diaNum)}`;
}

// Misma idea que sanearFechaInput, pero para fechas que pueden ser futuras
// (ej. citas agendadas): no acota el año al presente, solo corrige
// mes (01-12) y día según los días reales de ese mes.
function sanearFechaFutura(valor) {
  if (!valor) return '';
  const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return valor;
  let [, anio, mes, dia] = m;
  let mesNum = Math.min(Math.max(parseInt(mes, 10) || 1, 1), 12);
  const anioNum = Math.min(Math.max(parseInt(anio, 10) || 1900, 1900), 2100);
  const diasEnMes = new Date(anioNum, mesNum, 0).getDate();
  const diaNum = Math.min(Math.max(parseInt(dia, 10) || 1, 1), diasEnMes);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(anioNum)}-${pad(mesNum)}-${pad(diaNum)}`;
}

// ── Catálogos ────────────────────────────────────────────────
const PARENTESCOS = [
  'Cónyuge','Madre','Padre','Hijo/a','Hermano/a',
  'Abuelo/a','Tío/a','Primo/a','Amigo/a','Tutor legal','Otro',
];

const GRUPOS_SANGRE = ['A+','A-','B+','B-','AB+','AB-','O+','O-','Desconocido'];

// Tipos de documento de identidad
const TIPOS_DOC = [
  'DUI',
  'Partida de Nacimiento',
  'Pasaporte',
  'DPI (Guatemala)',
  'Carnet de Residente',
  'Carnet Diplomático',
  'Tarjeta de Seguro',
  'Sin documento',
];

// Tipos de ingreso — 10 tipos completos
const TIPOS_INGRESO = {
  Consulta: {
    label: 'Consulta externa', icono: '🩺',
    descripcion: 'Cita programada, chequeo médico, especialista, control periódico',
    subtipos: ['Cita programada','Chequeo médico general','Consulta especialista','Control periódico','Otro'],
    requiereHab: false, requiereClinica: true, esCita: true, soloRegistro: false,
  },
  Emergencia: {
    label: 'Emergencia', icono: '🚨',
    descripcion: 'Accidentes, crisis médicas, casos urgentes',
    subtipos: ['Accidente de tránsito','Accidente laboral','Crisis médica','Dolor agudo','Trauma','Intoxicación','Otro'],
    requiereHab: true, requiereClinica: false, esCita: false, soloRegistro: false,
  },
  Hospitalizacion: {
    label: 'Hospitalización programada', icono: '🏥',
    descripcion: 'Cirugía planificada, tratamiento largo, observación médica',
    subtipos: ['Cirugía planificada','Tratamiento oncológico','Observación médica','Procedimiento quirúrgico urgente','Otro'],
    requiereHab: true, requiereClinica: false, esCita: false, soloRegistro: false,
  },
  Maternidad: {
    label: 'Maternidad', icono: '🤱',
    descripcion: 'Trabajo de parto, cesárea, control obstétrico',
    subtipos: ['Trabajo de parto natural','Cesárea programada','Cesárea de emergencia','Control obstétrico','Otro'],
    requiereHab: true, requiereClinica: false, esCita: false, soloRegistro: false,
  },
  Referencia: {
    label: 'Referencia / Traslado', icono: '🚑',
    descripcion: 'Referido de otro hospital, traslado en ambulancia, remitido por clínica',
    subtipos: ['Referido de hospital público','Referido de clínica privada','Traslado en ambulancia','Referido de ISSS','Referido de Seguro Social','Otro'],
    requiereHab: true, requiereClinica: false, esCita: false, soloRegistro: false,
  },
  Reingreso: {
    label: 'Reingreso', icono: '🔄',
    descripcion: 'Paciente que regresa por seguimiento, control postoperatorio, revisión de tratamiento',
    subtipos: ['Control postoperatorio','Seguimiento de tratamiento','Revisión de herida','Complicación postoperatoria','Otro'],
    requiereHab: false, requiereClinica: true, esCita: true, soloRegistro: false,
  },
  TrasladoInterno: {
    label: 'Traslado interno', icono: '🔀',
    descripcion: 'Cambio de área dentro del hospital (Emergencia → Hospitalización → UCI)',
    subtipos: ['Emergencia → Hospitalización','Hospitalización → UCI','UCI → Hospitalización','Hospitalización → Maternidad','Otro'],
    requiereHab: true, requiereClinica: false, esCita: false, soloRegistro: false,
  },
  Examenes: {
    label: 'Exámenes / Diagnóstico', icono: '🔬',
    descripcion: 'Laboratorio, imágenes, estudios diagnósticos sin hospitalización',
    subtipos: ['Laboratorio clínico','Radiografía','Ultrasonido','Tomografía','Resonancia magnética','Electrocardiograma','Endoscopía','Otro'],
    requiereHab: false, requiereClinica: true, esCita: true, soloRegistro: false,
  },
  Legal: {
    label: 'Caso legal / Médico-legal', icono: '⚖️',
    descripcion: 'Accidente con reporte policial, evaluación médico-legal',
    subtipos: ['Accidente con reporte policial','Evaluación médico-legal','Violencia doméstica','Accidente laboral con denuncia','Pericia médica','Otro'],
    requiereHab: false, requiereClinica: false, esCita: false, soloRegistro: true,
  },
  HomeCare: {
    label: 'Atención domiciliaria', icono: '🏠',
    descripcion: 'Paciente que no se presenta físicamente, visitas médicas en casa',
    subtipos: ['Visita médica domiciliaria','Cuidados de enfermería en casa','Terapia física domiciliaria','Seguimiento post-alta en casa','Curación de heridas en casa','Otro'],
    requiereHab: false, requiereClinica: false, esCita: true, soloRegistro: true,
  },
};

// ── Estilos ──────────────────────────────────────────────────
const S = {
  pagina: { background: '#f0f4f8', minHeight: '100vh', padding: '20px', fontFamily: 'Segoe UI, sans-serif' },
  fila2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  fila3:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' },
  fila4:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' },
  filaDocumento: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px' },
  card: { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '20px', marginBottom: '16px' },
  cardTitulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '16px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  campo:    { marginBottom: '14px' },
  label:    { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d',  marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '.04em' },
  labelReq: { display: 'block', fontSize: '11px', fontWeight: '600', color: '#1B2A5E', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '.04em' },
  // Input con mayúsculas por defecto
  input: { width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #c0ccda', borderRadius: '7px', background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase' },
  inputFocus: { border: '1.5px solid #1B2A5E' },
  // Input especial para "Otro" subtipo
  inputLibre: { width: '100%', fontSize: '13px', padding: '8px 10px', border: '2px solid #ff9800', borderRadius: '7px', background: '#fff8e1', color: '#333', outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase' },
  btnPrimario:   { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: '7px', padding: '10px 24px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnSecundario: { background: '#fff', color: '#495057', border: '1px solid #c0ccda', borderRadius: '7px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer', marginLeft: '10px' },
  resultado: { border: '1px solid #dde3ea', borderRadius: '8px', marginTop: '6px', maxHeight: '200px', overflowY: 'auto', background: '#fff' },
  resultadoItem: { padding: '10px 14px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f0f4f8' },
  pacienteSeleccionado: { background: '#e8f0fb', borderRadius: '8px', padding: '14px', marginTop: '8px', fontSize: '13px' },
  alerta: (tipo) => ({
    padding: '12px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '500', marginBottom: '14px',
    background: tipo === 'ok' ? '#d4edda' : tipo === 'err' ? '#f8d7da' : '#fff3cd',
    color:      tipo === 'ok' ? '#155724' : tipo === 'err' ? '#721c24' : '#856404',
    border:     `1px solid ${tipo === 'ok' ? '#c3e6cb' : tipo === 'err' ? '#f5c6cb' : '#ffc107'}`,
  }),
  tag: (tipo) => {
    const map = { UCI: { bg: '#f8d7da', color: '#721c24' }, Privada: { bg: '#cce5ff', color: '#004085' }, General: { bg: '#d4edda', color: '#155724' }, Maternidad: { bg: '#f4c0d1', color: '#72243E' }, Pediatria: { bg: '#fff3cd', color: '#856404' } };
    const c = map[tipo] || { bg: '#e2e3e5', color: '#383d41' };
    return { display: 'inline-block', padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '600', background: c.bg, color: c.color, marginLeft: '6px' };
  },
  seccionFamiliar: { background: '#f8f0fb', border: '1px solid #d8c8ea', borderRadius: '8px', padding: '14px', marginBottom: '14px' },
  seccionRemitido: { background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', padding: '14px', marginBottom: '14px' },
  seccionLegal:    { background: '#fce4ec', border: '1px solid #f48fb1', borderRadius: '8px', padding: '14px', marginBottom: '14px' },
  tipoCard: (activo) => ({ border: `2px solid ${activo ? '#1B2A5E' : '#dde3ea'}`, borderRadius: '10px', padding: '12px 14px', cursor: 'pointer', background: activo ? '#e8f0fb' : '#fff', transition: 'all .15s' }),
  tipoIcono:        { fontSize: '22px', marginBottom: '4px' },
  tipoLabel: (activo) => ({ fontSize: '12px', fontWeight: '700', color: activo ? '#1B2A5E' : '#495057', marginBottom: '2px' }),
  tipoDesc:         { fontSize: '10px', color: '#6c757d', lineHeight: '1.3' },
  badge: (color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '10px', fontWeight: '700', background: color + '22', color: color, border: `1px solid ${color}44` }),
};

// ── Estado inicial ───────────────────────────────────────────
const VACÍO = {
  // Búsqueda
  busqueda: '', pacienteId: null, pacienteTexto: '',
  // Nuevo paciente
  nuevoNombre: '', nuevoApellido: '',
  tipoDocumento: 'DUI', nuevoDocumento: '',   // ← tipo + número (reemplaza nuevoDui)
  nuevoTelefono: '', nuevoFechaNac: '', nuevoSexo: '',
  nuevoDireccion: '', nuevoEmail: '',
  nuevoNacionalidad: 'Salvadoreño/a', nuevoSangre: '',
  // Responsable/familiar (contacto general del paciente)
  familiarNombre: '', familiarParentesco: '', familiarTelefono: '', familiarDireccion: '',
  familiarDui: '', familiarTipoDocumento: 'DUI',
  // Responsable legal de ESTA admisión (obligatorio si el paciente es menor)
  respAutorizaEsFamiliar: true,   // true = usa los datos del familiar de arriba
  responsableNombre: '', responsableParentesco: '', responsableDui: '',
  responsableTipoDocumento: 'DUI', responsableTelefono: '',
  responsableAutoriza: true, responsableGarantePago: true,
  // Admisión
  tipoIngreso: '', subtipoIngreso: '', subtipoLibre: '',
  habitacionId: '', clinicaId: '', medicoId: '',
  motivo: '', fechaCita: '', horaCita: '',
  // Remisión
  esRemitido: 'no',
  drRemite: '', especialidadRemite: '', institucionRemite: '', motivoRemision: '',
  motivoRemisionPre: '', // opción predefinida del select
  guardarMedicoRemite: false, // checkbox para guardar médico remitente
  // Caso legal
  tieneReportePolicial: false,
  numeroReporte: '', delegacionPolicial: '',
  // Home care
  direccionAtencion: '',
};


// ════════════════════════════════════════════════════════════
//  GENERADOR HTML — REPORTE DE ADMISIÓN
// ════════════════════════════════════════════════════════════
const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const fmtFecha = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? esc(v) : d.toLocaleDateString('es-SV');
};

const fmtFechaHora = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? esc(v) : d.toLocaleString('es-SV');
};

const filaHtml = (label, value) => `
  <div class="c">
    <span class="cl">${esc(label)}:</span>
    <span class="cv">${value ? esc(value) : '—'}</span>
  </div>
`;

function generarHTMLAdmision(adm) {
  const medico = [adm.medico_titulo, adm.medico_nombre, adm.medico_apellido]
    .filter(Boolean)
    .join(' ');

  const documento = adm.dui || adm.num_documento || '—';
  const tipoDocumento = adm.dui ? 'DUI' : (adm.tipo_documento || 'Documento');
  const tipoIngreso = TIPOS_INGRESO[adm.tipo_ingreso]?.label || adm.tipo_ingreso || '—';

  const bloques = [];

  bloques.push(`
    <div class="sec">
      <h3>Datos del paciente</h3>
      <div class="f2">
        <div>
          ${filaHtml('Código paciente', adm.paciente_codigo)}
          ${filaHtml('Paciente', adm.paciente)}
          ${filaHtml('Fecha de nacimiento', fmtFecha(adm.fecha_nac))}
          ${filaHtml('Edad', adm.fecha_nac ? `${calcularEdad(adm.fecha_nac)} años` : '—')}
          ${filaHtml('Sexo', adm.sexo)}
          ${filaHtml('Grupo sanguíneo', adm.grupo_sanguineo)}
        </div>
        <div>
          ${filaHtml('Tipo documento', tipoDocumento)}
          ${filaHtml('Número documento', documento)}
          ${filaHtml('Teléfono', adm.telefono)}
          ${filaHtml('Email', adm.email)}
          ${filaHtml('Dirección', adm.direccion)}
          ${filaHtml('Nacionalidad', adm.nacionalidad)}
        </div>
      </div>
    </div>
  `);

  bloques.push(`
    <div class="sec">
      <h3>Datos de la admisión</h3>
      <div class="f2">
        <div>
          ${filaHtml('No. admisión', adm.id)}
          ${filaHtml('Fecha ingreso', fmtFechaHora(adm.fecha_ingreso))}
          ${filaHtml('Estado', adm.estado)}
          ${filaHtml('Tipo ingreso', tipoIngreso)}
          ${filaHtml('Subtipo', adm.subtipo)}
          ${filaHtml('Motivo', adm.motivo)}
        </div>
        <div>
          ${filaHtml('Médico responsable', medico)}
          ${filaHtml('Clínica / consultorio', adm.clinica_nombre)}
          ${filaHtml('Habitación', adm.habitacion_num ? `${adm.habitacion_num} (${adm.habitacion_tipo || 'N/A'})` : '—')}
          ${filaHtml('Seguro', adm.seguro_nombre)}
          ${filaHtml('Fecha cita', fmtFecha(adm.fecha_cita))}
          ${filaHtml('Hora cita', adm.hora_cita)}
        </div>
      </div>
    </div>
  `);

  if (adm.familiar_nombre || adm.familiar_parentesco || adm.familiar_telefono || adm.familiar_direccion) {
    bloques.push(`
      <div class="sec">
        <h3>Responsable / contacto</h3>
        <div class="f2">
          <div>
            ${filaHtml('Nombre', adm.familiar_nombre)}
            ${filaHtml('Parentesco', adm.familiar_parentesco)}
          </div>
          <div>
            ${filaHtml('Teléfono', adm.familiar_telefono)}
            ${filaHtml('Dirección', adm.familiar_direccion)}
          </div>
        </div>
      </div>
    `);
  }

  if (adm.es_remitido) {
    bloques.push(`
      <div class="sec remitido">
        <h3>Referencia / remisión</h3>
        <div class="f2">
          <div>
            ${filaHtml('Dr./Dra. que remite', adm.dr_remite)}
            ${filaHtml('Especialidad', adm.especialidad_remite)}
          </div>
          <div>
            ${filaHtml('Institución', adm.institucion_remite)}
            ${filaHtml('Motivo remisión', adm.motivo_remision)}
          </div>
        </div>
      </div>
    `);
  }

  if (adm.tiene_reporte_policial || adm.numero_reporte || adm.delegacion_policial) {
    bloques.push(`
      <div class="sec legal">
        <h3>Información legal</h3>
        <div class="f2">
          <div>
            ${filaHtml('Reporte policial', adm.tiene_reporte_policial ? 'Sí' : 'No')}
            ${filaHtml('Número reporte', adm.numero_reporte)}
          </div>
          <div>
            ${filaHtml('Delegación policial', adm.delegacion_policial)}
            ${filaHtml('Dirección atención', adm.direccion_atencion)}
          </div>
        </div>
      </div>
    `);
  } else if (adm.direccion_atencion) {
    bloques.push(`
      <div class="sec">
        <h3>Atención domiciliaria</h3>
        ${filaHtml('Dirección de atención', adm.direccion_atencion)}
      </div>
    `);
  }

  return `<!DOCTYPE html>
  <html lang="es">
  <head>
    <meta charset="UTF-8"/>
    <title>Reporte de admisión ${esc(adm.id)}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Segoe UI',sans-serif;color:#222;padding:24px;font-size:12px}
      .cab{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px}
      .brand{font-size:18px;font-weight:800;color:#1B2A5E}
      .subbrand{font-size:9px;letter-spacing:2px;color:#1D9E75}
      .titulo{text-align:right}
      .titulo h1{font-size:16px;color:#1B2A5E;margin-bottom:4px}
      .titulo p{font-size:11px;color:#6c757d}
      hr{border:none;border-top:2px solid #1B2A5E;margin:8px 0 14px}
      .sec{border:1px solid #dde3ea;border-radius:8px;padding:12px 14px;margin-bottom:12px}
      .sec.remitido{background:#fff8e1;border-color:#ffe082}
      .sec.legal{background:#fce4ec;border-color:#f48fb1}
      h3{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#1B2A5E;margin-bottom:10px}
      .f2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
      .c{display:flex;gap:8px;margin-bottom:7px;align-items:flex-start}
      .cl{font-weight:700;min-width:140px;color:#495057;font-size:11px}
      .cv{flex:1;border-bottom:1px solid #ccd6e0;min-height:16px;padding-bottom:2px}
      .foot{margin-top:18px;font-size:10px;color:#6c757d;text-align:center}
      @media print{
        body{padding:10px}
        .sec{break-inside:avoid}
        .f2{grid-template-columns:1fr 1fr}
      }
    </style>
  </head>
  <body>
    <div class="cab">
      <div>
        <div class="brand">ALTIMO</div>
        <div class="subbrand">MEDICAL CENTER</div>
      </div>
      <div class="titulo">
        <h1>Reporte de admisión</h1>
        <p>Admisión #${esc(adm.id)} · Generado ${esc(new Date().toLocaleString('es-SV'))}</p>
      </div>
    </div>
    <hr/>
    ${bloques.join('')}
    <div class="foot">Documento generado automáticamente por el sistema ALTIMO Medical Center.</div>
  </body>
  </html>`;
}

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function Admision({ usuario, onAdmisionCreada }) {
  const [vistaModulo, setVistaModulo] = useState('registrar'); // 'registrar' | 'formularios'
  const [form,         setForm]         = useState(VACÍO);
  const [modo,         setModo]         = useState('buscar');
  const [resultados,   setResultados]   = useState([]);
  const [paciente,     setPaciente]     = useState(null);
  const [habitaciones, setHabitaciones] = useState([]);
  const [clinicas,     setClinicas]     = useState([]);
  const [medicos,      setMedicos]      = useState([]);
  const [medicosRemitentes, setMedicosRemitentes] = useState([]);
  const [mensaje,      setMensaje]      = useState(null);
  const [guardando,    setGuardando]    = useState(false);
  const [focusField,   setFocusField]   = useState(null);
  const [modalAlert,   setModalAlert]   = useState(null);
  const [expedienteOk, setExpedienteOk] = useState('');
  const [codigoPac,    setCodigoPac]    = useState('');
  const [pendingNavId, setPendingNavId] = useState(null);
  const [pdfGenerado,  setPdfGenerado]  = useState(false);
  const [pdfError,     setPdfError]     = useState('');

  // ── Panel de bienvenida — admisiones de hoy + camas libres ──
  // Admisión no tiene Dashboard (no está en PERMISOS.dashboard), así que
  // este resumen mínimo le da contexto al entrar sin exponerle datos
  // financieros (que no le corresponden, ver KPIs del Dashboard general).
  const [resumenHoy,        setResumenHoy]        = useState(null);
  const [cargandoResumen,   setCargandoResumen]   = useState(true);

  // Carga inicial
  useEffect(() => {
    async function cargar() {
      const [rHab, rMed, rCli, rMedR] = await Promise.all([
        api.habitaciones.listar(),
        api.medicos.listar(),
        api.clinicas.listar(),
        api.medicosRemitentes.listar(),
      ]);
      if (rHab.ok)  setHabitaciones(rHab.data.filter(h => h.estado === 'Libre'));
      if (rMed.ok)  setMedicos(rMed.data);
      if (rCli.ok)  setClinicas(rCli.data.filter(c => c.estado === 'Libre'));
      if (rMedR.ok) setMedicosRemitentes(rMedR.data || []);
    }
    cargar();
  }, []);

  // Resumen de bienvenida: reutiliza dashboard.kpis() (ya calcula
  // pacientesHoy y habitacionesLibres/Total) y se queda solo con esos 2
  // campos — ignora deliberadamente facturadoHoy y las alertas de
  // inventario, que no son del dominio de Admisión.
  const cargarResumenHoy = useCallback(async (silencioso = false) => {
    if (!silencioso) setCargandoResumen(true);
    try {
      const r = await api.dashboard.kpis();
      if (r.ok) {
        setResumenHoy({
          admisionesHoy:      r.data.pacientesHoy ?? 0,
          habitacionesLibres: r.data.habitacionesLibres ?? 0,
          habitacionesTotal:  r.data.habitacionesTotal ?? 0,
        });
      }
    } finally {
      setCargandoResumen(false);
    }
  }, []);

  useEffect(() => {
    cargarResumenHoy();
    const t = setInterval(() => cargarResumenHoy(true), 30000);
    return () => clearInterval(t);
  }, [cargarResumenHoy]);

  // Búsqueda de pacientes con debounce
  useEffect(() => {
    if (form.busqueda.length < 2) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const r = await api.pacientes.buscar(form.busqueda);
      if (r.ok) setResultados(r.data);
    }, 300);
    return () => clearTimeout(t);
  }, [form.busqueda]);

  // Helpers
  const set   = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setMU = (key, val) => setForm(f => ({ ...f, [key]: mayus(val) })); // con MAYÚSCULAS
  const inp   = (field) => ({ ...S.input, ...(focusField === field ? S.inputFocus : {}) });
  const foc   = (f)     => ({ onFocus: () => setFocusField(f), onBlur: () => setFocusField(null) });

  const seleccionarPaciente = (p) => {
    setPaciente(p);
    setForm(f => ({ ...f, busqueda: '', pacienteId: p.id, pacienteTexto: `${p.nombre} ${p.apellido}` }));
    setResultados([]);
  };

  const limpiar = () => {
    setForm(VACÍO); setPaciente(null); setResultados([]);
    setMensaje(null); setModo('buscar');
  };

  // Derivados del tipo de ingreso seleccionado
  const tipoMeta        = TIPOS_INGRESO[form.tipoIngreso] || null;
  const requiereHab     = tipoMeta?.requiereHab     || false;
  const requiereClinica = tipoMeta?.requiereClinica  || false;
  const esCita          = tipoMeta?.esCita           || false;
  const esLegal         = form.tipoIngreso === 'Legal';
  const esHomeCare      = form.tipoIngreso === 'HomeCare';
  const esSubtipoOtro   = form.subtipoIngreso === 'Otro';

  // Nombre del médico para el dropdown
  const nombreMedico = (m) => {
    const titulo = m.titulo ? `${m.titulo} ` : '';
    const apellido = m.apellido ? ` ${m.apellido}` : '';
    return `${titulo}${m.nombre}${apellido}`;
  };

  // Crear nuevo paciente
  const crearNuevoPaciente = async () => {
    // Menores de edad no poseen DUI en El Salvador. Si el tipo elegido es
    // "DUI" pero no se escribió número (caso típico al registrar un menor),
    // se guarda como "Sin documento" (N/A) en vez de un DUI vacío/heredado.
    const edadNuevoPac = form.nuevoFechaNac ? calcularEdad(form.nuevoFechaNac) : null;
    const esMenorNuevoPac = edadNuevoPac !== null && edadNuevoPac < 18;
    const tipoDocEfectivo = (esMenorNuevoPac && form.tipoDocumento === 'DUI' && !form.nuevoDocumento.trim())
      ? 'Sin documento'
      : form.tipoDocumento;

    // DUI solo si el tipo es DUI y tiene valor, si no → NULL
    const duiLimpio = tipoDocEfectivo === 'DUI'
      ? (form.nuevoDocumento.trim() || null)
      : null;

    const r = await api.pacientes.crear({
      nombre:              mayus(form.nuevoNombre),
      apellido:            mayus(form.nuevoApellido),
      dui:                 duiLimpio,
      tipo_documento:      tipoDocEfectivo !== 'DUI' ? tipoDocEfectivo : null,
      num_documento:       tipoDocEfectivo !== 'DUI' ? (form.nuevoDocumento.trim() || null) : null,
      telefono:            form.nuevoTelefono.trim()    || null,
      email:               form.nuevoEmail.trim()       || null,
      fecha_nac:           form.nuevoFechaNac           || null,
      sexo:                form.nuevoSexo               || null,
      direccion:           mayus(form.nuevoDireccion)   || null,
      nacionalidad:        mayus(form.nuevoNacionalidad)|| null,
      grupo_sanguineo:     form.nuevoSangre             || null,
      familiar_nombre:     mayus(form.familiarNombre)   || null,
      familiar_parentesco: form.familiarParentesco       || null,
      familiar_telefono:   form.familiarTelefono.trim() || null,
      familiar_direccion:  mayus(form.familiarDireccion)|| null,
      familiar_dui:        form.familiarDui.trim()      || null,
      familiar_tipo_documento: form.familiarTipoDocumento || null,
    });
    if (!r.ok) throw new Error(r.error);
    const rGet = await api.pacientes.obtener(Number(r.data?.lastInsertRowid ?? r.data?.id));
    return rGet.data;
  };

  // Guardar admisión
  const handleGuardar = async () => {
    const erroresValidacion = [];

    if (modo === 'buscar' && !form.pacienteId) {
      erroresValidacion.push('Selecciona un paciente o crea uno nuevo.');
    }
    if (modo === 'nuevo' && (!form.nuevoNombre || !form.nuevoApellido)) {
      erroresValidacion.push('Nombre y apellido son obligatorios.');
    }
    if (!form.tipoIngreso) {
      erroresValidacion.push('Selecciona el tipo de ingreso.');
    }
    if (tipoMeta && !form.subtipoIngreso) {
      erroresValidacion.push('Selecciona el subtipo / motivo específico.');
    }
    if (esSubtipoOtro && !form.subtipoLibre.trim()) {
      erroresValidacion.push('Describe el tipo de caso específico (campo "Otro").');
    }
    if (!form.medicoId && !esHomeCare) {
      erroresValidacion.push('Selecciona el médico responsable.');
    }
    if (requiereClinica && !form.clinicaId) {
      erroresValidacion.push('Selecciona el consultorio / clínica.');
    }
    if (esCita && !form.fechaCita) {
      erroresValidacion.push('Indica la fecha de la cita.');
    }
    if (form.esRemitido === 'si' && !form.drRemite) {
      erroresValidacion.push('Indica el nombre del médico que remite.');
    }
    if (esLegal && form.tieneReportePolicial && !form.numeroReporte) {
      erroresValidacion.push('Ingresa el número del reporte policial.');
    }
    if (esMenor) {
      const nombreResp = form.respAutorizaEsFamiliar ? form.familiarNombre : form.responsableNombre;
      const duiResp     = form.respAutorizaEsFamiliar ? form.familiarDui    : form.responsableDui;
      const tipoDocResp = form.respAutorizaEsFamiliar ? form.familiarTipoDocumento : form.responsableTipoDocumento;
      if (!nombreResp || !nombreResp.trim()) {
        erroresValidacion.push('El paciente es menor de edad: el nombre del responsable legal es obligatorio.');
      }
      if (!form.respAutorizaEsFamiliar && !form.familiarParentesco && !form.responsableParentesco) {
        erroresValidacion.push('Indica el parentesco del responsable legal del menor.');
      }
      if (tipoDocResp === 'DUI' && (!duiResp || !duiResp.trim())) {
        erroresValidacion.push('El paciente es menor de edad: el DUI del responsable legal es obligatorio para validar la autorización.');
      }
    }

    if (erroresValidacion.length > 0) {
      const texto = erroresValidacion.length === 1
        ? erroresValidacion[0]
        : `Hay ${erroresValidacion.length} campos por corregir:\n\n` + erroresValidacion.map(e => `• ${e}`).join('\n');
      setModalAlert({ tipo: 'warn', texto });
      return;
    }

    // Subtipo final
    const subtipoFinal = esSubtipoOtro
      ? mayus(form.subtipoLibre.trim())
      : form.subtipoIngreso || null;

    setGuardando(true);
    try {
      let pacienteId = form.pacienteId;
      if (modo === 'nuevo') {
        const p = await crearNuevoPaciente();
        pacienteId = p.id;
      }

      const r = await api.admisiones.crear({
        paciente_id:   pacienteId,
        habitacion_id: requiereHab && form.habitacionId ? Number(form.habitacionId) : null,
        clinica_id:    requiereClinica && form.clinicaId ? Number(form.clinicaId) : null,
        medico_id:     form.medicoId ? Number(form.medicoId) : null,
        tipo_ingreso:  form.tipoIngreso,
        subtipo:       subtipoFinal,
        motivo:        mayus(form.motivo) || null,
        fecha_cita:    esCita ? form.fechaCita || null : null,
        hora_cita:     esCita ? form.horaCita  || null : null,
        es_remitido:         form.esRemitido === 'si' ? 1 : 0,
        dr_remite:           form.esRemitido === 'si' ? mayus(form.drRemite) || null : null,
        especialidad_remite: form.esRemitido === 'si' ? mayus(form.especialidadRemite) || null : null,
        institucion_remite:  form.esRemitido === 'si' ? mayus(form.institucionRemite)  || null : null,
        motivo_remision:     form.esRemitido === 'si' ? mayus(form.motivoRemision)     || null : null,
        tiene_reporte_policial: esLegal && form.tieneReportePolicial ? 1 : 0,
        numero_reporte:         esLegal ? form.numeroReporte || null : null,
        delegacion_policial:    esLegal ? mayus(form.delegacionPolicial) || null : null,
        direccion_atencion:     esHomeCare ? mayus(form.direccionAtencion) || null : null,
        usuario_id: usuario.id,
        // Responsable legal — solo se envía cuando el paciente es menor de edad
        responsable_nombre:      esMenor ? mayus(form.respAutorizaEsFamiliar ? form.familiarNombre : form.responsableNombre) || null : null,
        responsable_parentesco:  esMenor ? (form.respAutorizaEsFamiliar ? form.familiarParentesco : form.responsableParentesco) || null : null,
        responsable_dui:         esMenor ? (form.respAutorizaEsFamiliar ? form.familiarDui.trim() : form.responsableDui.trim()) || null : null,
        responsable_tipo_documento: esMenor ? (form.respAutorizaEsFamiliar ? form.familiarTipoDocumento : form.responsableTipoDocumento) || null : null,
        responsable_telefono:   esMenor ? (form.respAutorizaEsFamiliar ? form.familiarTelefono.trim() : form.responsableTelefono.trim()) || null : null,
        responsable_autoriza_procedimientos: esMenor ? !!form.responsableAutoriza : true,
        responsable_garante_pago: esMenor ? !!form.responsableGarantePago : true,
      });

      if (!r.ok) throw new Error(r.error);

      // Guardar médico remitente si se marcó la opción
      if (form.esRemitido === 'si' && form.guardarMedicoRemite && form.drRemite.trim()) {
        try {
          await api.medicosRemitentes.guardar({
            nombre:       mayus(form.drRemite.trim()),
            especialidad: form.especialidadRemite.trim() || null,
            institucion:  form.institucionRemite.trim()  || null,
          });
          // Recargar lista
          const rMedR = await api.medicosRemitentes.listar();
          if (rMedR?.ok) setMedicosRemitentes(rMedR.data || []);
        } catch(_) {}
      }

      const numAdm = String(Number(r.data?.lastInsertRowid ?? r.data?.id ?? ''));
      const codPac = r.data?.codigo || '';

      setPdfGenerado(false);
      setPdfError('');

      try {
        const rAdm = await api.admisiones.obtener(Number(numAdm));
        const admData = rAdm?.data || rAdm;

        if (admData?.id) {
          const htmlAdm = generarHTMLAdmision(admData);

          await api.admisiones.guardarPdfDisco({
            admision_id: Number(numAdm),
            html: htmlAdm,
          });

          setPdfGenerado(true);
        } else {
          setPdfError('No se pudieron cargar los datos completos de la admisión para generar el PDF.');
        }
      } catch (ePdf) {
        setPdfError(ePdf.message || 'No se pudo generar el PDF de admisión.');
      }

      // ⚠️ ORDEN CRÍTICO: primero mostrar el modal, navegar sólo al hacer clic en Aceptar
      // Si se llama onAdmisionCreada aquí, React navega y desmonta el componente
      // antes de que el modal se renderice → el usuario nunca lo ve.
      setPendingNavId(Number(numAdm));   // guardar para navegar después
      setExpedienteOk(numAdm);           // activa el modal
      setCodigoPac(codPac);

    } catch (e) {
      setModalAlert({ tipo: 'err', texto: e.message });
    }
    setGuardando(false);
  };

  const fechaNacActiva = modo === 'nuevo' ? form.nuevoFechaNac : paciente?.fecha_nac;
  const edad    = fechaNacActiva ? calcularEdad(fechaNacActiva) : null;
  const esMenor = edad !== null && edad < 18;

  // ── RENDER ──────────────────────────────────────────────────
  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* ══ Header módulo — estandarizado con Clínico/Enfermería/Laboratorio ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          👤 Módulo de Admisión
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', marginBottom:'12px' }}>
          Registro de pacientes · Habitaciones · Formularios de ingreso
        </div>
        <div style={{ display:'flex', gap:'2px' }}>
          {[
            { id: 'registrar',   label: '👤 Registrar admisión' },
            { id: 'formularios', label: '📋 Formularios de admisión' },
          ].map(t => (
            <button key={t.id}
              onClick={() => setVistaModulo(t.id)}
              style={{ padding:'9px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                background: vistaModulo === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      vistaModulo === t.id ? '#1B2A5E' : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={S.pagina}>

      {/* ══════════════════════════════════════════════════════
          PANEL DE BIENVENIDA — admisiones de hoy + camas libres.
          Solo datos operativos propios de Admisión; sin montos
          financieros (eso es de caja/admin, ver Dashboard general).
      ══════════════════════════════════════════════════════ */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>
        <div style={{ ...S.card, marginBottom:0, display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontSize:'28px' }}>👤</div>
          <div>
            <div style={{ fontSize:'26px', fontWeight:'800', color:'#1B2A5E', lineHeight:1 }}>
              {cargandoResumen ? '—' : (resumenHoy?.admisionesHoy ?? 0)}
            </div>
            <div style={{ fontSize:'11px', color:'#6c757d', fontWeight:'600', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
              Admisiones registradas hoy
            </div>
          </div>
        </div>
        <div style={{ ...S.card, marginBottom:0, display:'flex', alignItems:'center', gap:'14px' }}>
          <div style={{ fontSize:'28px' }}>🛏</div>
          <div>
            <div style={{ fontSize:'26px', fontWeight:'800', color: (resumenHoy?.habitacionesLibres ?? 0) === 0 ? '#dc3545' : '#1D9E75', lineHeight:1 }}>
              {cargandoResumen ? '—' : `${resumenHoy?.habitacionesLibres ?? 0} / ${resumenHoy?.habitacionesTotal ?? 0}`}
            </div>
            <div style={{ fontSize:'11px', color:'#6c757d', fontWeight:'600', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
              Camas libres disponibles
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          VISTA — FORMULARIOS DE ADMISIÓN
          (Pagaré, Consentimiento informado, Ingreso/Egreso,
           Ingreso de enfermería)
      ══════════════════════════════════════════════════════ */}
      {vistaModulo === 'formularios' && (
        <Formularios usuario={usuario} areaFiltro="admision" pacienteIdInicial={paciente?.id || null} />
      )}

      {/* ══════════════════════════════════════════════════════
          VISTA — REGISTRAR ADMISIÓN (flujo original)
      ══════════════════════════════════════════════════════ */}
      {vistaModulo === 'registrar' && (
      <>

      {/* ══ MODAL ALERTA — Validaciones y errores ══ */}
      {modalAlert && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:'16px', padding:'36px 40px', maxWidth:'420px', width:'90%', boxShadow:'0 12px 48px rgba(0,0,0,.3)', textAlign:'center' }}>
            <div style={{ fontSize:'44px', marginBottom:'12px' }}>
              {modalAlert.tipo === 'err' ? '❌' : '⚠️'}
            </div>
            <div style={{ fontSize:'16px', fontWeight:'800', marginBottom:'10px',
              color: modalAlert.tipo === 'err' ? '#721c24' : '#856404' }}>
              {modalAlert.tipo === 'err' ? 'Error al registrar' : 'Campo requerido'}
            </div>
            <div style={{ fontSize:'14px', color:'#333', lineHeight:'1.6', marginBottom:'24px', whiteSpace:'pre-line', textAlign: modalAlert.texto.includes('\n') ? 'left' : 'center' }}>
              {modalAlert.texto}
            </div>
            <button
              style={{ background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'8px', padding:'11px 36px', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}
              onClick={() => setModalAlert(null)}>
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* ══ MODAL ÉXITO — Admisión procesada ══ */}
      {expedienteOk !== '' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:'16px', padding:'40px 44px', maxWidth:'480px', width:'90%', boxShadow:'0 12px 48px rgba(0,0,0,.3)', textAlign:'center' }}>
            <div style={{ fontSize:'52px', marginBottom:'10px' }}>✅</div>
            <div style={{ fontSize:'18px', fontWeight:'800', color:'#155724', marginBottom:'6px' }}>
              ¡Admisión procesada!
            </div>
            <div style={{ fontSize:'12px', color:'#6c757d', marginBottom:'16px' }}>
              Anote el código del paciente para localizarlo en Formularios, Laboratorio y Facturación:
            </div>

            {/* Código del paciente — formato NNNN/AAAA — ES EL IDENTIFICADOR ÚNICO */}
            {codigoPac && (
              <div style={{ marginBottom:'20px' }}>
                <div style={{ fontSize:'11px', color:'#6c757d', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.05em' }}>Código del paciente</div>
                <div style={{ fontSize:'44px', fontWeight:'900', color:'#1D9E75', letterSpacing:'3px', border:'3px solid #1D9E75', borderRadius:'12px', padding:'8px 24px', display:'inline-block', background:'#d4edda' }}>
                  {codigoPac}
                </div>
                <div style={{ fontSize:'11px', color:'#adb5bd', marginTop:'8px' }}>
                  Use este código para buscar al paciente en Formularios, Laboratorio y Facturación.
                </div>
              </div>
            )}

            {/* Estado del PDF */}
            <div style={{ marginBottom:'20px' }}>
              {pdfGenerado ? (
                <div style={{
                  background:'#d4edda',
                  border:'1px solid #c3e6cb',
                  color:'#155724',
                  borderRadius:'10px',
                  padding:'12px 16px',
                  fontSize:'13px',
                  fontWeight:'700'
                }}>
                  📄 PDF de admisión generado correctamente
                </div>
              ) : pdfError ? (
                <div style={{
                  background:'#fff3cd',
                  border:'1px solid #ffc107',
                  color:'#856404',
                  borderRadius:'10px',
                  padding:'12px 16px',
                  fontSize:'13px',
                  fontWeight:'700',
                  lineHeight:'1.5'
                }}>
                  ⚠ La admisión se guardó, pero el PDF no se pudo generar.<br/>
                  <span style={{ fontWeight:'500' }}>{pdfError}</span>
                </div>
              ) : (
                <div style={{
                  background:'#e8f0fb',
                  border:'1px solid #c8ddf7',
                  color:'#1B2A5E',
                  borderRadius:'10px',
                  padding:'12px 16px',
                  fontSize:'13px',
                  fontWeight:'700'
                }}>
                  ⏳ Procesando PDF de admisión...
                </div>
              )}
            </div>

            <button
              style={{ background:'#1D9E75', color:'#fff', border:'none', borderRadius:'8px', padding:'12px 36px', fontSize:'15px', fontWeight:'700', cursor:'pointer' }}
              onClick={() => {
                setExpedienteOk('');
                setCodigoPac('');
                setPendingNavId(null);
                setPdfGenerado(false);
                setPdfError('');
                limpiar();
              }}>
              Aceptar
            </button>
          </div>
        </div>
      )}

      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      {/* ══════════════════════════════════════════════════════
          PASO 1 — PACIENTE
      ══════════════════════════════════════════════════════ */}
      <div style={S.card}>
        <div style={S.cardTitulo}>
          <span>👤</span> Paso 1 — Datos del paciente
        </div>

        {/* Tabs buscar / nuevo */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {['buscar','nuevo'].map(m => (
            <button key={m}
              onClick={() => { setModo(m); setPaciente(null); setForm(f => ({ ...f, pacienteId: null })); }}
              style={{ padding: '7px 18px', borderRadius: '7px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: 'none', background: modo === m ? '#1B2A5E' : '#e8f0fb', color: modo === m ? '#fff' : '#1B2A5E' }}>
              {m === 'buscar' ? '🔍 Buscar paciente existente' : '➕ Nuevo paciente'}
            </button>
          ))}
        </div>

        {/* ── Búsqueda ── */}
        {modo === 'buscar' && (
          <>
            <div style={S.campo}>
              <label style={S.label}>Buscar por nombre, apellido, DUI o código</label>
              <input style={{ ...inp('busqueda'), textTransform: 'none' }} value={form.busqueda}
                onChange={e => set('busqueda', e.target.value)}
                {...foc('busqueda')} placeholder="Escribe nombre, DUI o código de paciente..." />
            </div>
            {resultados.length > 0 && (
              <div style={S.resultado}>
                {resultados.map(p => (
                  <div key={p.id} style={{ ...S.resultadoItem, display:'flex', justifyContent:'space-between', alignItems:'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4f8'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div onDoubleClick={() => seleccionarPaciente(p)} style={{ flex:1, cursor:'default' }}>
                      <strong>{p.nombre} {p.apellido}</strong>
                      {p.codigo && (
                        <span style={{ fontFamily:'monospace', fontSize:'11px', background:'#e8f0fb', color:'#1B2A5E', padding:'1px 7px', borderRadius:'4px', fontWeight:'700', marginLeft:'8px' }}>
                          {p.codigo}
                        </span>
                      )}
                      <span style={{ color: '#6c757d', fontSize: '12px', marginLeft: '8px' }}>
                        {p.dui ? `DUI: ${p.dui}` : 'Sin DUI'} · Tel: {p.telefono || 'N/A'}
                      </span>
                    </div>
                    <button
                      onClick={() => seleccionarPaciente(p)}
                      style={{ marginLeft:'10px', background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'6px', padding:'5px 14px', fontSize:'12px', fontWeight:'700', cursor:'pointer', flexShrink:0 }}>
                      Seleccionar
                    </button>
                  </div>
                ))}
              </div>
            )}
            {paciente && (
              <div style={S.pacienteSeleccionado}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <strong style={{ fontSize: '14px', color: '#1B2A5E' }}>{paciente.nombre} {paciente.apellido}</strong>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                      {paciente.dui ? `DUI: ${paciente.dui}` : 'Sin DUI'} · Tel: {paciente.telefono || '—'}
                      {paciente.direccion ? ` · ${paciente.direccion}` : ''}
                    </div>
                    {paciente.familiar_nombre && (
                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                        Responsable: {paciente.familiar_nombre} ({paciente.familiar_parentesco || '—'})
                      </div>
                    )}
                  </div>
                  <span style={{ color: '#1D9E75', fontWeight: '700', fontSize: '12px' }}>✓ Seleccionado</span>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Nuevo paciente ── */}
        {modo === 'nuevo' && (
          <>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#1B2A5E', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '10px' }}>
              Datos personales
            </div>

            {/* Nombre y apellido */}
            <div style={S.fila3}>
              <div style={S.campo}>
                <label style={S.labelReq}>Nombres *</label>
                <input style={inp('nuevoNombre')} value={form.nuevoNombre}
                  onChange={e => setMU('nuevoNombre', e.target.value)}
                  {...foc('nuevoNombre')} placeholder="PRIMER Y SEGUNDO NOMBRE" />
              </div>
              <div style={S.campo}>
                <label style={S.labelReq}>Apellidos *</label>
                <input style={inp('nuevoApellido')} value={form.nuevoApellido}
                  onChange={e => setMU('nuevoApellido', e.target.value)}
                  {...foc('nuevoApellido')} placeholder="PRIMER Y SEGUNDO APELLIDO" />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Sexo</label>
                <select style={inp('nuevoSexo')} value={form.nuevoSexo}
                  onChange={e => set('nuevoSexo', e.target.value)} {...foc('nuevoSexo')}>
                  <option value="">— Seleccionar —</option>
                  <option value="M">Masculino</option>
                  <option value="F">Femenino</option>
                </select>
              </div>
            </div>

            {/* Tipo de documento — NUEVO */}
            <div style={S.filaDocumento}>
              <div style={S.campo}>
                <label style={S.label}>Tipo de documento de identidad</label>
                <select style={inp('tipoDocumento')} value={form.tipoDocumento}
                  onChange={e => { set('tipoDocumento', e.target.value); set('nuevoDocumento', ''); }}
                  {...foc('tipoDocumento')}>
                  {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={S.campo}>
                <label style={S.label}>
                  {form.tipoDocumento === 'DUI'           ? 'Número de DUI'
                  : form.tipoDocumento === 'Sin documento' ? 'Sin documento de identidad'
                  : `Número de ${form.tipoDocumento}`}
                </label>
                <input
                  style={inp('nuevoDocumento')}
                  value={form.nuevoDocumento}
                  onChange={e => set('nuevoDocumento', e.target.value)}
                  {...foc('nuevoDocumento')}
                  placeholder={
                    form.tipoDocumento === 'DUI'           ? '00000000-0'
                    : form.tipoDocumento === 'Sin documento' ? 'No aplica'
                    : `Número de ${form.tipoDocumento}`
                  }
                  disabled={form.tipoDocumento === 'Sin documento'}
                />
              </div>
            </div>

            {/* Fecha nac, edad, grupo sanguíneo */}
            <div style={S.fila4}>
              <div style={S.campo}>
                <label style={S.label}>Fecha de nacimiento</label>
                <input type="date" style={{ ...inp('nuevoFechaNac'), textTransform: 'none' }}
                  value={form.nuevoFechaNac}
                  onChange={e => set('nuevoFechaNac', e.target.value)}
                  onBlur={e => set('nuevoFechaNac', sanearFechaInput(e.target.value))}
                  min="1900-01-01" max={new Date().toISOString().slice(0, 10)}
                  {...foc('nuevoFechaNac')} />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Edad {edad !== null ? `→ ${edad} años` : ''}</label>
                <div style={{ ...S.input, background: '#f8fafd', color: '#1B2A5E', fontWeight: '600' }}>
                  {edad !== null ? `${edad} años` : '— automático'}
                </div>
              </div>
              <div style={S.campo}>
                <label style={S.label}>Grupo sanguíneo</label>
                <select style={inp('nuevoSangre')} value={form.nuevoSangre}
                  onChange={e => set('nuevoSangre', e.target.value)} {...foc('nuevoSangre')}>
                  <option value="">— Seleccionar —</option>
                  {GRUPOS_SANGRE.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div style={S.campo}>
                <label style={S.label}>Teléfono</label>
                <input style={{ ...inp('nuevoTelefono'), textTransform: 'none' }}
                  value={form.nuevoTelefono}
                  onChange={e => set('nuevoTelefono', e.target.value)}
                  {...foc('nuevoTelefono')} placeholder="0000-0000" />
              </div>
            </div>

            {/* Correo, dirección, nacionalidad */}
            <div style={S.fila3}>
              <div style={S.campo}>
                <label style={S.label}>Correo electrónico</label>
                <input style={{ ...inp('nuevoEmail'), textTransform: 'lowercase' }}
                  value={form.nuevoEmail}
                  onChange={e => set('nuevoEmail', e.target.value.toLowerCase())}
                  {...foc('nuevoEmail')} placeholder="correo@ejemplo.com" type="email" />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Nacionalidad</label>
                <input style={inp('nuevoNacionalidad')} value={form.nuevoNacionalidad}
                  onChange={e => setMU('nuevoNacionalidad', e.target.value)}
                  {...foc('nuevoNacionalidad')} />
              </div>
              <div style={S.campo}>
                {/* espacio */}
              </div>
            </div>

            <div style={S.campo}>
              <label style={S.label}>Dirección de residencia</label>
              <input style={inp('nuevoDireccion')} value={form.nuevoDireccion}
                onChange={e => setMU('nuevoDireccion', e.target.value)}
                {...foc('nuevoDireccion')} placeholder="COL. X, CALLE Y, MUNICIPIO, DEPARTAMENTO" />
            </div>

            {/* Familiar / Responsable */}
            <div style={S.seccionFamiliar}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#6c3b9e', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '12px' }}>
                👤 Familiar o responsable
              </div>
              <div style={S.fila3}>
                <div style={S.campo}>
                  <label style={S.label}>Nombre completo</label>
                  <input style={inp('familiarNombre')} value={form.familiarNombre}
                    onChange={e => setMU('familiarNombre', e.target.value)}
                    {...foc('familiarNombre')} placeholder="NOMBRE DEL RESPONSABLE" />
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Parentesco</label>
                  <select style={inp('familiarParentesco')} value={form.familiarParentesco}
                    onChange={e => set('familiarParentesco', e.target.value)} {...foc('familiarParentesco')}>
                    <option value="">— Seleccionar —</option>
                    {PARENTESCOS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Teléfono de contacto</label>
                  <input style={{ ...inp('familiarTelefono'), textTransform: 'none' }}
                    value={form.familiarTelefono}
                    onChange={e => set('familiarTelefono', e.target.value)}
                    {...foc('familiarTelefono')} placeholder="0000-0000" />
                </div>
              </div>
              <div style={S.campo}>
                <label style={{ ...S.label, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span>Dirección del familiar / responsable</span>
                  {form.nuevoDireccion && (
                    <button
                      type="button"
                      onClick={() => setMU('familiarDireccion', form.nuevoDireccion)}
                      style={{ background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'5px', padding:'2px 10px', fontSize:'10px', fontWeight:'700', cursor:'pointer', letterSpacing:'.03em' }}>
                      📋 Igual que paciente
                    </button>
                  )}
                </label>
                <input style={inp('familiarDireccion')} value={form.familiarDireccion}
                  onChange={e => setMU('familiarDireccion', e.target.value)}
                  {...foc('familiarDireccion')} placeholder="DIRECCIÓN DEL RESPONSABLE" />
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════
            RESPONSABLE LEGAL — obligatorio si el paciente es menor
            Aplica tanto en modo "buscar" (paciente existente) como
            "nuevo". Se fija por ADMISIÓN, no se asume del perfil.
        ══════════════════════════════════════════════════ */}
        {esMenor && (
          <div style={{ ...S.seccionFamiliar, border:'1px solid #f5c6cb', background:'#fff8f8', marginTop:'14px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#a02030', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '4px' }}>
              ⚖️ Responsable legal del menor ({edad} {edad === 1 ? 'año' : 'años'}) — Obligatorio
            </div>
            <div style={{ fontSize: '11px', color: '#856404', marginBottom: '12px', lineHeight: '1.4' }}>
              Por ser el paciente menor de edad, debe registrarse quién autoriza procedimientos
              clínicos y/o garantiza el pago de esta atención, conforme a la legislación vigente.
            </div>

            {modo === 'nuevo' && (
              <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', marginBottom:'12px', cursor:'pointer' }}>
                <input type="checkbox" checked={form.respAutorizaEsFamiliar}
                  onChange={e => set('respAutorizaEsFamiliar', e.target.checked)} />
                El familiar/responsable registrado arriba es quien autoriza y garantiza esta admisión
              </label>
            )}

            {(modo === 'buscar' || !form.respAutorizaEsFamiliar) && (
              <div style={S.fila3}>
                <div style={S.campo}>
                  <label style={S.labelReq}>Nombre del responsable</label>
                  <input style={inp('responsableNombre')} value={form.responsableNombre}
                    onChange={e => setMU('responsableNombre', e.target.value)}
                    {...foc('responsableNombre')} placeholder="NOMBRE DE MADRE/PADRE/TUTOR" />
                </div>
                <div style={S.campo}>
                  <label style={S.labelReq}>Parentesco</label>
                  <select style={inp('responsableParentesco')} value={form.responsableParentesco}
                    onChange={e => set('responsableParentesco', e.target.value)} {...foc('responsableParentesco')}>
                    <option value="">— Seleccionar —</option>
                    {PARENTESCOS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Teléfono</label>
                  <input style={{ ...inp('responsableTelefono'), textTransform: 'none' }}
                    value={form.responsableTelefono}
                    onChange={e => set('responsableTelefono', e.target.value)}
                    {...foc('responsableTelefono')} placeholder="0000-0000" />
                </div>
              </div>
            )}

            {(modo === 'buscar' || !form.respAutorizaEsFamiliar) && (
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={S.label}>Tipo de documento del responsable</label>
                  <select style={inp('responsableTipoDocumento')} value={form.responsableTipoDocumento}
                    onChange={e => set('responsableTipoDocumento', e.target.value)} {...foc('responsableTipoDocumento')}>
                    {TIPOS_DOC.filter(t => t !== 'Partida de Nacimiento').map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.campo}>
                  <label style={form.responsableTipoDocumento === 'DUI' ? S.labelReq : S.label}>
                    {form.responsableTipoDocumento === 'DUI' ? 'Número de DUI' : 'Número de documento'}
                  </label>
                  <input style={inp('responsableDui')} value={form.responsableDui}
                    onChange={e => set('responsableDui', e.target.value)}
                    {...foc('responsableDui')} placeholder="00000000-0" />
                </div>
              </div>
            )}

            {modo === 'nuevo' && form.respAutorizaEsFamiliar && (
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={S.labelReq}>Tipo de documento del responsable</label>
                  <select style={inp('familiarTipoDocumento')} value={form.familiarTipoDocumento}
                    onChange={e => set('familiarTipoDocumento', e.target.value)} {...foc('familiarTipoDocumento')}>
                    {TIPOS_DOC.filter(t => t !== 'Partida de Nacimiento').map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.campo}>
                  <label style={form.familiarTipoDocumento === 'DUI' ? S.labelReq : S.label}>
                    {form.familiarTipoDocumento === 'DUI' ? 'Número de DUI' : 'Número de documento'}
                  </label>
                  <input style={inp('familiarDui')} value={form.familiarDui}
                    onChange={e => set('familiarDui', e.target.value)}
                    {...foc('familiarDui')} placeholder="00000000-0" />
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:'20px', marginTop:'10px', flexWrap:'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:'7px', fontSize:'12px', cursor:'pointer' }}>
                <input type="checkbox" checked={form.responsableAutoriza}
                  onChange={e => set('responsableAutoriza', e.target.checked)} />
                Autoriza procedimientos médicos
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:'7px', fontSize:'12px', cursor:'pointer' }}>
                <input type="checkbox" checked={form.responsableGarantePago}
                  onChange={e => set('responsableGarantePago', e.target.checked)} />
                Es garante de los cargos generados
                <TooltipAyuda texto="Indica que esta persona es responsable de cubrir los costos de esta atención. Aparecerá como garante en la cuenta y facturación del paciente." />
              </label>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          PASO 2 — TIPO DE INGRESO
      ══════════════════════════════════════════════════════ */}
      <div style={S.card}>
        <div style={S.cardTitulo}>
          <span>📋</span> Paso 2 — Tipo de ingreso *
        </div>

        {/* Grid de tipos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '16px' }}>
          {Object.entries(TIPOS_INGRESO).map(([key, meta]) => (
            <div key={key}
              style={S.tipoCard(form.tipoIngreso === key)}
              onClick={() => setForm(f => ({ ...f, tipoIngreso: key, subtipoIngreso: '', subtipoLibre: '', habitacionId: '', clinicaId: '' }))}>
              <div style={S.tipoIcono}>{meta.icono}</div>
              <div style={S.tipoLabel(form.tipoIngreso === key)}>{meta.label}</div>
              <div style={S.tipoDesc}>{meta.descripcion}</div>
            </div>
          ))}
        </div>

        {/* Subtipo y médico */}
        {tipoMeta && (
          <div style={S.fila2}>
            <div style={S.campo}>
              <label style={S.labelReq}>Subtipo / Motivo específico</label>
              <select style={inp('subtipoIngreso')} value={form.subtipoIngreso}
                onChange={e => { set('subtipoIngreso', e.target.value); if (e.target.value !== 'Otro') set('subtipoLibre', ''); }}
                {...foc('subtipoIngreso')}>
                <option value="">— Seleccionar subtipo —</option>
                {tipoMeta.subtipos.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={S.campo}>
              <label style={S.label}>Médico responsable {!esHomeCare ? '*' : '(opcional)'}</label>
              <select style={inp('medicoId')} value={form.medicoId}
                onChange={e => set('medicoId', e.target.value)} {...foc('medicoId')}>
                <option value="">— Seleccionar —</option>
                {medicos.map(m => <option key={m.id} value={m.id}>{nombreMedico(m)}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* ── Campo libre cuando subtipo es "Otro" — NUEVO ── */}
        {esSubtipoOtro && (
          <div style={{ ...S.campo, background: '#fff8e1', border: '2px solid #ffe082', borderRadius: '8px', padding: '12px' }}>
            <label style={{ ...S.labelReq, color: '#e65100' }}>Especifica el tipo de caso *</label>
            <input
              style={S.inputLibre}
              value={form.subtipoLibre}
              onChange={e => setMU('subtipoLibre', e.target.value)}
              placeholder="DESCRIBE EL TIPO DE CASO..."
              autoFocus
            />
          </div>
        )}

        {/* Fecha/hora de cita */}
        {esCita && (
          <div style={S.fila3}>
            <div style={S.campo}>
              <label style={S.labelReq}>Fecha de la cita *</label>
              <input type="date" style={{ ...inp('fechaCita'), textTransform: 'none' }}
                value={form.fechaCita} min="1900-01-01" max="2100-12-31"
                onChange={e => set('fechaCita', sanearFechaFutura(e.target.value))} {...foc('fechaCita')} />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Hora de la cita</label>
              <input type="time" style={{ ...inp('horaCita'), textTransform: 'none' }}
                value={form.horaCita}
                onChange={e => set('horaCita', e.target.value)} {...foc('horaCita')} />
            </div>
            <div />
          </div>
        )}

        {/* Clínica */}
        {requiereClinica && (
          <div style={S.campo}>
            <label style={S.labelReq}>Consultorio / Clínica *</label>
            <select style={inp('clinicaId')} value={form.clinicaId}
              onChange={e => set('clinicaId', e.target.value)} {...foc('clinicaId')}>
              <option value="">— Seleccionar consultorio —</option>
              {clinicas.map(c => (
                <option key={c.id} value={c.id}>{c.nombre}{c.especialidad ? ` · ${c.especialidad}` : ''}</option>
              ))}
            </select>
            {clinicas.length === 0 && <span style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px', display: 'block' }}>No hay consultorios disponibles</span>}
          </div>
        )}

        {/* Habitación */}
        {requiereHab && (
          <div style={S.campo}>
            <label style={S.labelReq}>Habitación / Área</label>
            <select style={inp('habitacionId')} value={form.habitacionId}
              onChange={e => set('habitacionId', e.target.value)} {...foc('habitacionId')}>
              <option value="">— Seleccionar habitación —</option>
              {habitaciones.map(h => <option key={h.id} value={h.id}>{h.numero} · {h.tipo}{h.estado==='Otra' && h.uso_otro ? ` · ${h.uso_otro}` : ''}</option>)}
            </select>
            {habitaciones.length === 0 && <span style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px', display: 'block' }}>No hay habitaciones disponibles</span>}
          </div>
        )}

        {/* Dirección Home Care */}
        {esHomeCare && (
          <div style={S.campo}>
            <label style={S.labelReq}>Dirección de atención domiciliaria *</label>
            <input style={inp('direccionAtencion')} value={form.direccionAtencion}
              onChange={e => setMU('direccionAtencion', e.target.value)}
              {...foc('direccionAtencion')} placeholder="COL. X, CALLE Y, CASA Z, MUNICIPIO" />
          </div>
        )}

        {/* Motivo de ingreso */}
        {tipoMeta && (
          <div style={S.campo}>
            <label style={S.label}>Descripción / Motivo de ingreso</label>
            <textarea
              style={{ ...inp('motivo'), resize: 'vertical', minHeight: '70px' }}
              value={form.motivo}
              onChange={e => setMU('motivo', e.target.value)}
              {...foc('motivo')}
              placeholder="DESCRIPCIÓN DEL MOTIVO DE INGRESO..." />
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          PASO 3 — ¿ES REMITIDO?
      ══════════════════════════════════════════════════════ */}
      {tipoMeta && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>↗️</span> Paso 3 — ¿Es remitido / referido?
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            {[{ v: 'no', label: 'No — Ingreso directo' }, { v: 'si', label: 'Sí — Es remitido' }].map(({ v, label }) => (
              <button key={v} onClick={() => set('esRemitido', v)}
                style={{ padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none',
                  background: form.esRemitido === v ? (v === 'si' ? '#e67e22' : '#1B2A5E') : '#e8f0fb',
                  color: form.esRemitido === v ? '#fff' : '#1B2A5E' }}>
                {label}
              </button>
            ))}
          </div>

          {form.esRemitido === 'si' && (
            <div style={S.seccionRemitido}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#b7690a', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '12px' }}>
                📋 Datos de la remisión
              </div>

              {/* ── Médicos guardados ── */}
              {medicosRemitentes.length > 0 && (
                <div style={{ ...S.campo, background: '#fff3e0', border: '1px solid #ffe082', borderRadius: '7px', padding: '10px 12px', marginBottom: '14px' }}>
                  <label style={{ ...S.label, color: '#e65100' }}>📂 Seleccionar médico remitente guardado</label>
                  <select
                    style={{ ...S.input, background: '#fffde7' }}
                    value=""
                    onChange={e => {
                      const id = Number(e.target.value);
                      const mr = medicosRemitentes.find(m => m.id === id);
                      if (mr) {
                        setMU('drRemite', mr.nombre);
                        if (mr.especialidad) setMU('especialidadRemite', mr.especialidad);
                        if (mr.institucion)  setMU('institucionRemite',  mr.institucion);
                      }
                    }}>
                    <option value="">— Elegir de la lista guardada —</option>
                    {medicosRemitentes.map(mr => (
                      <option key={mr.id} value={mr.id}>
                        {mr.nombre}{mr.especialidad ? ` · ${mr.especialidad}` : ''}{mr.institucion ? ` | ${mr.institucion}` : ''}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '10px', color: '#795548', marginTop: '4px' }}>
                    Al seleccionar uno, se autocompletan los campos abajo.
                  </div>
                </div>
              )}

              <div style={S.fila3}>
                <div style={S.campo}>
                  <label style={S.labelReq}>Dr./Dra. que remite *</label>
                  <input style={inp('drRemite')} value={form.drRemite}
                    onChange={e => setMU('drRemite', e.target.value)}
                    {...foc('drRemite')} placeholder="NOMBRE COMPLETO DEL MÉDICO" />
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Especialidad del médico</label>
                  <input style={inp('especialidadRemite')} value={form.especialidadRemite}
                    onChange={e => setMU('especialidadRemite', e.target.value)}
                    {...foc('especialidadRemite')} placeholder="EJ: CARDIOLOGÍA, CIRUGÍA..." />
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Institución / Hospital de origen</label>
                  <input style={inp('institucionRemite')} value={form.institucionRemite}
                    onChange={e => setMU('institucionRemite', e.target.value)}
                    {...foc('institucionRemite')} placeholder="NOMBRE DEL HOSPITAL O CLÍNICA" />
                </div>
              </div>

              {/* ── Motivo de remisión con opciones predefinidas ── */}
              <div style={S.campo}>
                <label style={S.label}>Motivo de la remisión</label>
                <select
                  style={inp('motivoRemisionPre')}
                  value={form.motivoRemisionPre}
                  onChange={e => {
                    const v = e.target.value;
                    set('motivoRemisionPre', v);
                    if (v !== 'Otro' && v !== '') setMU('motivoRemision', v);
                    else if (v === '') setMU('motivoRemision', '');
                  }}
                  {...foc('motivoRemisionPre')}>
                  <option value="">— Seleccionar motivo —</option>
                  {[
                    'Evaluación especializada',
                    'Procedimiento quirúrgico',
                    'Manejo de urgencia / emergencia',
                    'Control y seguimiento',
                    'Diagnóstico por imagen',
                    'Exámenes de laboratorio',
                    'Hospitalización programada',
                    'Segunda opinión médica',
                    'Manejo de dolor crónico',
                    'Rehabilitación y fisioterapia',
                    'Atención obstétrica / maternidad',
                    'Atención pediátrica especializada',
                    'Complicación postoperatoria',
                    'Otro',
                  ].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {(form.motivoRemisionPre === 'Otro' || form.motivoRemisionPre === '') && (
                  <textarea
                    style={{ ...inp('motivoRemision'), resize: 'vertical', minHeight: '60px', marginTop: '6px' }}
                    value={form.motivoRemision}
                    onChange={e => setMU('motivoRemision', e.target.value)}
                    {...foc('motivoRemision')}
                    placeholder="DESCRIPCIÓN DEL MOTIVO POR EL QUE FUE REMITIDO..." />
                )}
                {form.motivoRemisionPre !== '' && form.motivoRemisionPre !== 'Otro' && (
                  <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '7px 12px', marginTop: '6px', fontSize: '12px', color: '#2e7d32', fontWeight: '600' }}>
                    ✓ {form.motivoRemision}
                  </div>
                )}
              </div>

              {/* ── Guardar médico para uso futuro ── */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', padding: '8px 12px', background: '#e8f5e9', borderRadius: '7px', border: '1px solid #c8e6c9' }}>
                <input type="checkbox" id="guardarMedR" style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  checked={form.guardarMedicoRemite}
                  onChange={e => set('guardarMedicoRemite', e.target.checked)} />
                <label htmlFor="guardarMedR" style={{ fontSize: '13px', fontWeight: '600', color: '#2e7d32', cursor: 'pointer' }}>
                  💾 Guardar este médico en el catálogo para futuras remisiones
                </label>
              </div>

              {/* Info de asignación automática */}
              <div style={{ background: '#fff8e1', borderRadius: '7px', padding: '10px 14px', fontSize: '12px', color: '#795548', marginTop: '12px' }}>
                <strong>ℹ️ Asignación automática según tipo:</strong>
                {requiereHab     && ' → Se asignará habitación (procedimiento quirúrgico / hospitalización).'}
                {requiereClinica && ' → Se asignará consultorio (consulta / examen).'}
                {!requiereHab && !requiereClinica && ' → Solo registro (caso legal / atención domiciliaria).'}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          PASO 4 — CASO LEGAL (condicional)
      ══════════════════════════════════════════════════════ */}
      {esLegal && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>⚖️</span> Paso 4 — Datos del caso legal
          </div>
          <div style={S.seccionLegal}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#c62828', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: '12px' }}>
              ⚠️ Información legal — Confidencial
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <input type="checkbox" id="reporte"
                checked={form.tieneReportePolicial}
                onChange={e => set('tieneReportePolicial', e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
              <label htmlFor="reporte" style={{ fontSize: '13px', fontWeight: '600', color: '#c62828', cursor: 'pointer' }}>
                Hay reporte policial asociado
              </label>
            </div>
            {form.tieneReportePolicial && (
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={S.labelReq}>Número de reporte policial *</label>
                  <input style={inp('numeroReporte')} value={form.numeroReporte}
                    onChange={e => set('numeroReporte', e.target.value)}
                    {...foc('numeroReporte')} placeholder="NÚMERO DE EXPEDIENTE POLICIAL" />
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Delegación policial</label>
                  <input style={inp('delegacionPolicial')} value={form.delegacionPolicial}
                    onChange={e => setMU('delegacionPolicial', e.target.value)}
                    {...foc('delegacionPolicial')} placeholder="DELEGACIÓN O SUBDELEGACIÓN" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          REFERENCIAS VISUALES — Habitaciones
      ══════════════════════════════════════════════════════ */}
      {requiereHab && habitaciones.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>🛏</span> Habitaciones libres ({habitaciones.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
            {habitaciones.map(h => (
              <div key={h.id}
                onClick={() => set('habitacionId', String(h.id))}
                style={{ border: `2px solid ${form.habitacionId === String(h.id) ? '#1B2A5E' : '#dde3ea'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', background: form.habitacionId === String(h.id) ? '#e8f0fb' : '#fff' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#1B2A5E' }}>{h.numero}</div>
                <span style={S.tag(h.tipo)}>{h.tipo}</span>
                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>{h.estado==='Otra' && h.uso_otro ? h.uso_otro : h.tipo}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          REFERENCIAS VISUALES — Clínicas
      ══════════════════════════════════════════════════════ */}
      {requiereClinica && clinicas.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>🏢</span> Consultorios disponibles ({clinicas.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {clinicas.map(c => (
              <div key={c.id}
                onClick={() => set('clinicaId', String(c.id))}
                style={{ border: `2px solid ${form.clinicaId === String(c.id) ? '#1B2A5E' : '#dde3ea'}`, borderRadius: '8px', padding: '12px', cursor: 'pointer', background: form.clinicaId === String(c.id) ? '#e8f0fb' : '#fff' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1B2A5E', marginBottom: '4px' }}>{c.nombre}</div>
                {c.especialidad && <div style={{ fontSize: '11px', color: '#6c757d' }}>{c.especialidad}</div>}
                <div style={{ marginTop: '6px' }}><span style={S.badge('#1D9E75')}>Disponible</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          BOTONES FINALES
      ══════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button style={S.btnPrimario} onClick={handleGuardar} disabled={guardando}>
          {guardando ? 'Guardando...' : '✓ Confirmar admisión'}
        </button>
        <button style={S.btnSecundario} onClick={limpiar}>🗑 Limpiar</button>

        {tipoMeta && (
          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>
            {tipoMeta.icono} <strong>{tipoMeta.label}</strong>
            {form.subtipoIngreso && ` → ${esSubtipoOtro ? (form.subtipoLibre || 'Otro') : form.subtipoIngreso}`}
            {form.esRemitido === 'si' && ' · Remitido'}
          </span>
        )}
      </div>

      </>
      )}
      </div>
    </div>
  );
}
